import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';

/**
 * DallasMavericksGames — a single centered headline + blurb + Score Tickets
 * CTA describing the Weidner resident discount on Dallas Mavericks NBA home
 * game tickets. Layout mirrors QualifyingCriteria (this theme's other single-
 * blurb interior page) so the Mavericks page reads under the same rhythm as
 * the rest of the Aster interior pages, and the CTA is skinned with the same
 * secondary-color pill button used by HeroSolidBG.
 */

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Score Tickets CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: 'https://mavs.spinzo.com/?group=corporate-group-offers-f6xp',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Score Tickets',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

export function DallasMavericksGames() {
  const ctaLink = useSlotGroup('dallasmavericksgames.cta_link', ctaLinkDef);
  const target = ctaLink._target || '_blank';

  return (
    <section className="pt-10 pb-16 md:pt-14 md:pb-24 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl md:max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="dallasmavericksgames.headline"
          label="Headline"
          as="div"
          className="font-heading text-xl sm:text-2xl md:text-3xl font-normal text-[var(--vibe-color-text)] mb-6 [&_p]:m-0"
        />
        <RichTextContent
          path="dallasmavericksgames.blurb"
          label="Page Blurb"
          as="div"
          className="font-body text-sm sm:text-base leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-4 [&_p:last-child]:mb-0"
        />

        <div className="mt-10">
          <EditableSlotGroup groupPath="dallasmavericksgames.cta_link" definition={ctaLinkDef}>
            <a
              href={ctaLink.url || 'https://mavs.spinzo.com/?group=corporate-group-offers-f6xp'}
              target={target}
              rel={target === '_blank' ? 'noopener noreferrer' : undefined}
              className="dmg-cta-btn"
            >
              <span className="dmg-cta-label">{ctaLink.label || 'Score Tickets'}</span>
              <svg
                className="dmg-cta-arrow"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3"
                />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .dmg-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 1rem 2.5rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          color: #ffffff;
          background: var(--vibe-color-secondary);
          border: 1.5px solid var(--vibe-color-secondary);
          border-radius: 2px;
          transition: transform 0.3s ease, background 0.3s ease, color 0.3s ease;
        }
        .dmg-cta-btn:hover {
          background: #ffffff;
          color: var(--vibe-color-secondary);
          transform: translateY(-2px);
        }
        .dmg-cta-btn:active {
          transform: translateY(0);
        }
        .dmg-cta-arrow {
          transition: transform 0.3s ease;
        }
        .dmg-cta-btn:hover .dmg-cta-arrow {
          transform: translateX(3px);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'DallasMavericksGames',
  displayName: 'Dallas Mavericks Games',
  tokens: {
    'dallasmavericksgames.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue:
        '<p>Go Mavs, Go <span data-type="field-token" data-field="property_name"></span>!</p>',
    },
    'dallasmavericksgames.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, we\'re all about the Mavs! Residents can score discounted Dallas Mavericks NBA home game tickets throughout the season at the best price. Residents can get up to 20% off tickets at the upper level, up to 15% off tickets at the Platinum level, and up to 10% off tickets at the lower level. Residents can buy a maximum of 20 tickets per game. Each game is subject to availability.</p>',
    },
  },
  slotGroups: {
    'dallasmavericksgames.cta_link': ctaLinkDef,
  },
};

(DallasMavericksGames as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default DallasMavericksGames;
